/**
 * The student-facing report — UX/UI Specification §10.
 *
 * One template serves the wizard preview and the PDF. There is no second
 * design: if the two could diverge, the studio would be sending clients a
 * document nobody reviewed (Architecture §12.1).
 *
 * A pure function of the snapshot (§16.1): no fetch, no clock, no globals, no
 * randomness. Images are addressed through `resolveImage`, which returns an
 * authorised media URL for the preview and a data: URI for the PDF.
 *
 * Sections never reorder, and a section with no data does not render at all —
 * not empty, not greyed, not as a placeholder (§10.2).
 */

import {
  formatDateLong,
  formatDateShort,
  formatDelta,
  formatPercent,
  formatPeriod,
  formatValue,
  paletteToCssVars,
  getOrnamentKey,
  getPalette,
  type OrnamentKey,
  type ReportSnapshot,
  type SnapshotPhoto,
  type SnapshotPhotoPair,
  type SnapshotPostureAssessment,
} from '@pp/shared';
import { PostureOverlay } from './PostureOverlay.js';
import type { CSSProperties, ReactNode } from 'react';
import { Icon, assessmentIcon, goalIcon, metricIcon } from './Icon.js';
import type { IconName } from './Icon.js';

export interface ReportDocumentProps {
  snapshot: ReportSnapshot;
  /**
   * Resolves a photo to something an <img> can load. The preview returns
   * /api/media/…; the PDF renderer returns a data: URI, because the headless
   * browser carries no session cookie (§15.4).
   */
  resolveImage: (photo: SnapshotPhoto) => string | null;
  /** The studio logo, resolved the same way. */
  logoUrl?: string | null;
}

/*
 * Two, not three. Each full-body frame is 240px tall (report.css
 * .pp-photo--body) plus its caption, and three of them under the section
 * title ran 63px past the bottom of the page in testing — the footer's page
 * number then printed on top of the third pair's photos instead of below
 * them. Two pairs leaves comfortable room; a student with all three
 * categories (front/side/back) gets the third on its own page via the same
 * chunk() below.
 */
const FULL_BODY_PER_PAGE = 2;
const REGIONAL_PER_PAGE = 4;
const GOALS_PER_PAGE = 5;

function chunk<T>(items: T[], size: number): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += size) pages.push(items.slice(i, i + size));
  return pages;
}

/* --------------------------------------------------------------- ornament */

/**
 * The corner motif, in whichever shape the studio chose — §6.2.
 *
 * Five drawings, one contract. Every one is tinted by the palette through
 * `currentColor` and carries no colour of its own, which is what D-07 was
 * guarding against when it said one shared set: the cost to avoid was
 * commissioning artwork per palette, not offering a choice of outline.
 *
 * Each fills the same 190×150 box in the top-right corner, so a studio
 * switching motifs never changes where anything else on the page sits. Masses
 * go in `__leaves` and hairlines in `__stems`, so the two opacities in the
 * stylesheet apply to all five without the drawings knowing about them.
 */
const ORNAMENT_ART: Record<OrnamentKey, ReactNode> = {
  // The original: a foliate spray.
  yaprak: (
    <>
      <g className="pp-ornament__leaves">
        <path d="M188 4c-30 6-56 22-74 45-9 12-15 25-19 39 12-3 24-9 34-17 20-16 35-40 44-67z" />
        <path d="M186 44c-24 2-46 12-63 29-8 8-15 18-20 29 11-1 21-5 30-11 18-12 33-30 43-47z" />
        <path d="M150 2c-18 14-32 32-40 53-4 11-7 22-7 33 9-6 16-15 21-24 10-19 18-40 26-62z" />
      </g>
      <g className="pp-ornament__stems">
        <path d="M188 6c-34 12-62 34-82 63" fill="none" strokeWidth="1.4" />
        <path d="M152 4c-16 20-27 44-32 69" fill="none" strokeWidth="1.2" />
      </g>
    </>
  ),

  // Nested currents — movement, without anything botanical.
  dalga: (
    <>
      <g className="pp-ornament__leaves">
        <path d="M190 0v54c-28 4-52 20-70 46-10 15-16 32-18 50h-26c2-30 12-58 31-81C127 43 156 24 190 0z" />
      </g>
      <g className="pp-ornament__stems">
        <path
          d="M190 66c-30 6-55 24-72 50-9 14-15 30-18 46"
          fill="none"
          strokeWidth="1.4"
        />
        <path
          d="M190 86c-25 7-46 24-60 46-8 12-13 26-16 40"
          fill="none"
          strokeWidth="1.2"
        />
        <path
          d="M190 106c-19 8-35 22-46 40-6 10-10 21-13 32"
          fill="none"
          strokeWidth="1"
        />
      </g>
    </>
  ),

  // Overlapping discs — calm, and the quietest of the five.
  ay: (
    <>
      <g className="pp-ornament__leaves">
        <circle cx="150" cy="42" r="42" />
      </g>
      <g className="pp-ornament__stems">
        <circle cx="150" cy="42" r="60" fill="none" strokeWidth="1.4" />
        <circle cx="150" cy="42" r="78" fill="none" strokeWidth="1" />
      </g>
    </>
  ),

  // Concentric arches — architectural, and the most formal.
  ark: (
    <>
      <g className="pp-ornament__leaves">
        <path d="M190 0v96h-38a38 38 0 0 1 38-38z" />
      </g>
      <g className="pp-ornament__stems">
        <path d="M190 40a56 56 0 0 0-56 56" fill="none" strokeWidth="1.4" />
        <path d="M190 18a78 78 0 0 0-78 78" fill="none" strokeWidth="1.2" />
        <path d="M112 118h78" fill="none" strokeWidth="1" />
      </g>
    </>
  ),

  // Nothing at all, for a studio that wants an undecorated page.
  yok: null,
};

/*
 * Exported, so the studio settings screen shows the real drawing rather than a
 * second copy of it. A picker that previewed one shape and printed another
 * would be worse than a picker with no preview at all.
 */
export function CornerOrnament({ ornamentKey }: { ornamentKey: OrnamentKey }) {
  const art = ORNAMENT_ART[ornamentKey];
  if (!art) return null;

  return (
    <svg
      className="pp-ornament"
      width="190"
      height="150"
      viewBox="0 0 190 150"
      aria-hidden="true"
    >
      {art}
    </svg>
  );
}

/**
 * A section heading with its mark.
 *
 * The icon is decorative — the words carry the meaning — but it is what makes
 * a reader recognise a page before reading it, which is most of what the
 * product owner asked for on 10 September 2026.
 */
function SectionTitle({
  icon,
  children,
  subtitle,
}: {
  icon: IconName;
  children: ReactNode;
  subtitle?: string;
}) {
  return (
    <>
      <h2 className="pp-title">
        <Icon name={icon} size={26} className="pp-title__icon" />
        {children}
      </h2>
      {subtitle && <p className="pp-subtitle">{subtitle}</p>}
      <Divider />
    </>
  );
}

function Divider() {
  return (
    <svg
      className="pp-divider"
      width="90"
      height="10"
      viewBox="0 0 90 10"
      aria-hidden="true"
    >
      <line x1="0" y1="5" x2="34" y2="5" strokeWidth="1" />
      <path d="M45 0l3 5-3 5-3-5z" />
      <line x1="56" y1="5" x2="90" y2="5" strokeWidth="1" />
    </svg>
  );
}

/* ----------------------------------------------------------------- pieces */

function Page({
  children,
  index,
  total,
  snapshot,
  logoUrl,
  ornamentKey,
  ornament = false,
  header = true,
}: {
  children: ReactNode;
  index: number;
  total: number;
  snapshot: ReportSnapshot;
  logoUrl: string | null | undefined;
  ornamentKey: OrnamentKey;
  ornament?: boolean;
  header?: boolean;
}) {
  return (
    <section className="pp-page">
      {ornament && <CornerOrnament ornamentKey={ornamentKey} />}
      {header && <PageHeader snapshot={snapshot} logoUrl={logoUrl} />}
      <div className="pp-page__body">{children}</div>
      <Footer snapshot={snapshot} page={index} total={total} />
    </section>
  );
}

/**
 * The running head, carrying the studio's mark — asked for on 10 September
 * 2026, so a report reads as the studio's letterhead on every page rather than
 * only on its cover.
 *
 * The logo replaces the studio name here rather than sitting beside it: a mark
 * and its own wordmark printed together is the commonest way a letterhead goes
 * wrong. A studio with no logo keeps the name, in the same place.
 */
function PageHeader({
  snapshot,
  logoUrl,
}: {
  snapshot: ReportSnapshot;
  logoUrl: string | null | undefined;
}) {
  // The same 120×60 box the cover's mark sits in at its larger size (§6.3),
  // not a bespoke smaller one: one component, one rule for how a logo scales
  // without distortion, used at both sizes rather than reimplemented at one
  // of them.
  return (
    <header className="pp-pagehead">
      <Brand snapshot={snapshot} logoUrl={logoUrl} />
      <span className="pp-pagehead__meta">
        {snapshot.student.fullName} ·{' '}
        {formatPeriod(snapshot.period.start, snapshot.period.end)}
      </span>
    </header>
  );
}

/**
 * Only the contact details the studio actually has. A studio with just a phone
 * number shows just a phone number, centred — the row reserves no empty slots
 * (§10.3).
 */
function Footer({
  snapshot,
  page,
  total,
}: {
  snapshot: ReportSnapshot;
  page: number;
  total: number;
}) {
  /*
   * Each contact keeps its own mark. A row of four bare strings made a reader
   * work out which was which, and an Instagram handle beside a website beside
   * an address is exactly the row where that is hardest.
   *
   * Order is fixed rather than following the studio's data, so every page of
   * every report puts the phone number in the same place. Only the details a
   * studio actually has are printed; the row reserves no empty slots (§10.3).
   */
  const contactEntries: [IconName, string | null][] = [
    ['telefon', snapshot.studio.phone],
    ['instagram', snapshot.studio.instagram],
    ['web', snapshot.studio.website],
    ['eposta', snapshot.studio.email],
  ];
  const contacts = contactEntries.filter(
    (entry): entry is [IconName, string] => entry[1] !== null,
  );

  return (
    <footer className="pp-footer">
      {contacts.length > 0 && (
        <div className="pp-footer__contacts">
          {contacts.map(([icon, value]) => (
            <span className="pp-footer__contact" key={value}>
              <Icon name={icon} size={13} />
              {value}
            </span>
          ))}
        </div>
      )}
      <div className="pp-footer__page">
        <Divider />
        <span>{page}</span>
        <Divider />
      </div>
      {/* No product name, no logo, no watermark, no URL that is not the
          studio's own — Constitution §8. */}
      <span className="pp-visually-hidden">{`${page} / ${total}`}</span>
    </footer>
  );
}

function Brand({
  snapshot,
  logoUrl,
  large = false,
}: {
  snapshot: ReportSnapshot;
  logoUrl: string | null | undefined;
  large?: boolean;
}) {
  if (logoUrl) {
    return (
      <div className={large ? 'pp-brand pp-brand--large' : 'pp-brand'}>
        <img src={logoUrl} alt={snapshot.studio.name} />
      </div>
    );
  }
  // Without a logo the studio name fills the same box, so the layout does not
  // shift (§6.3).
  return (
    <div className={large ? 'pp-brand pp-brand--large' : 'pp-brand'}>
      <span className="pp-brand__name">{snapshot.studio.name}</span>
    </div>
  );
}

function PhotoFrame({
  photo,
  label,
  alt,
  resolveImage,
  variant,
}: {
  photo: SnapshotPhoto;
  label: string;
  alt: string;
  resolveImage: ReportDocumentProps['resolveImage'];
  variant: 'body' | 'regional';
}) {
  const src = resolveImage(photo);
  return (
    <figure className={`pp-photo pp-photo--${variant}`}>
      <span className="pp-photo__label">{label}</span>
      <div className="pp-photo__frame">
        {src && <img className="pp-photo__img" src={src} alt={alt} />}
      </div>
      {/* A comparison without dates is not evidence of anything (§15.3). */}
      <figcaption className="pp-photo__date">
        {formatDateLong(photo.capturedAt)}
      </figcaption>
    </figure>
  );
}

function PosturePhotoFrame({
  photo,
  resolveImage,
}: {
  photo: SnapshotPostureAssessment['photos'][number];
  resolveImage: ReportDocumentProps['resolveImage'];
}) {
  const src = resolveImage(photo.photo);
  return (
    <figure className="pp-posture-photo">
      <span className="pp-photo__label">{photo.label}</span>
      <div className="pp-posture-photo__frame">
        {src && <img className="pp-photo__img" src={src} alt={photo.label} />}
        <div className="pp-posture-photo__overlay">
          <PostureOverlay linePositionPercent={photo.linePositionPercent} />
        </div>
      </div>
    </figure>
  );
}

function PosturePageSingle({
  assessment,
  resolveImage,
}: {
  assessment: SnapshotPostureAssessment;
  resolveImage: ReportDocumentProps['resolveImage'];
}) {
  return (
    <>
      {assessment.photos.length > 0 && (
        <div className="pp-posture-photos">
          {assessment.photos.map((photo) => (
            <PosturePhotoFrame
              photo={photo}
              resolveImage={resolveImage}
              key={photo.photo.photoId}
            />
          ))}
        </div>
      )}

      {assessment.score !== null && (
        <div className="pp-posture-score">
          <span className="pp-note__label">Genel Postür</span>
          <ScoreBar value={assessment.score} />
        </div>
      )}

      {assessment.note && (
        <div className="pp-card pp-note">
          <span className="pp-note__label">Eğitmen Notu</span>
          <p>{assessment.note}</p>
        </div>
      )}
    </>
  );
}

function PhotoPair({
  pair,
  resolveImage,
  variant,
}: {
  pair: SnapshotPhotoPair;
  resolveImage: ReportDocumentProps['resolveImage'];
  variant: 'body' | 'regional';
}) {
  return (
    <div className={`pp-pair pp-pair--${variant}`}>
      <span className="pp-pair__label">{pair.label}</span>
      <div className="pp-pair__frames">
        {pair.left && (
          <>
            <PhotoFrame
              photo={pair.left}
              label={pair.leftLabel}
              alt={`${pair.label}, ${formatDateLong(pair.left.capturedAt)}`}
              resolveImage={resolveImage}
              variant={variant}
            />
            <span className="pp-pair__arrow" aria-hidden="true">
              →
            </span>
          </>
        )}
        <PhotoFrame
          photo={pair.right}
          label={pair.rightLabel}
          alt={`${pair.label}, ${formatDateLong(pair.right.capturedAt)}`}
          resolveImage={resolveImage}
          variant={variant}
        />
      </div>
    </div>
  );
}

/**
 * The chart is laid out at exactly the size it is drawn at (§13.3). Two per
 * row inside the page inset: (794 - 2*48 - 14) / 2, less the card's padding
 * and border. Scaling the viewBox to fit instead would scale the labels too,
 * and §4.3 admits nothing under 10px in a report a student reads on a phone.
 */
const CHART_W = 316;
const CHART_H = 160;

function TrendChart({ chart }: { chart: ReportSnapshot['charts'][number] }) {
  const W = CHART_W;
  const H = CHART_H;
  const PAD = { top: 28, right: 16, bottom: 22, left: 38 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const span = chart.yMax - chart.yMin || 1;

  const x = (i: number) =>
    chart.points.length === 1
      ? PAD.left + plotW / 2
      : PAD.left + (i / (chart.points.length - 1)) * plotW;
  const y = (v: number) => PAD.top + plotH - ((v - chart.yMin) / span) * plotH;

  const grid: number[] = [];
  for (let v = chart.yMin; v <= chart.yMax + 1e-9; v += chart.yStep) {
    grid.push(Number(v.toFixed(6)));
  }

  const anchor = (i: number): 'start' | 'middle' | 'end' =>
    i === 0 ? 'start' : i === chart.points.length - 1 ? 'end' : 'middle';

  return (
    <figure className="pp-chart">
      <figcaption className="pp-chart__title">
        {chart.unit ? `${chart.label} (${chart.unit})` : chart.label}
        {/* A chart that outran its own report's dates has to say so, or it is
            the silent leak the period rule exists to prevent. */}
        {chart.spansAllTime && <em className="pp-chart__scope">tüm geçmiş</em>}
      </figcaption>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        className="pp-chart__svg"
        role="img"
        aria-label={`${chart.label}: ${chart.points
          .map(
            (p) => `${p.axisLabel} ${formatValue(p.value, chart.unit, chart.decimals)}`,
          )
          .join(', ')}`}
      >
        {grid.map((v) => (
          <g key={v}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(v)}
              y2={y(v)}
              className="pp-chart__grid"
            />
            <text x={PAD.left - 7} y={y(v) + 4} className="pp-chart__tick">
              {formatValue(v, '', chart.decimals)}
            </text>
          </g>
        ))}
        {/* No smoothing: a spline through sparse body measurements invents
            values that were never taken (§13.3). */}
        {/* Dashed between exactly two points: the straight segment between
            them is the one part of the chart nobody measured (§13.3). */}
        <polyline
          className={
            chart.points.length === 2
              ? 'pp-chart__line pp-chart__line--sparse'
              : 'pp-chart__line'
          }
          fill="none"
          points={chart.points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ')}
        />
        {chart.points.map((p, i) => (
          <g key={p.measuredAt + i}>
            <circle cx={x(i)} cy={y(p.value)} r={3} className="pp-chart__point" />
            <text
              x={x(i)}
              y={y(p.value) - 9}
              textAnchor={anchor(i)}
              className="pp-chart__value"
            >
              {formatValue(p.value, '', chart.decimals)}
            </text>
            <text x={x(i)} y={H - 6} textAnchor={anchor(i)} className="pp-chart__axis">
              {p.axisLabel}
            </text>
          </g>
        ))}
      </svg>
    </figure>
  );
}

function CoverMessage({ snapshot }: { snapshot: ReportSnapshot }) {
  if (!snapshot.coverMessage) return null;
  return (
    <div className="pp-message pp-card">
      <svg
        className="pp-message__mark"
        viewBox="0 0 24 24"
        width="26"
        height="26"
        aria-hidden="true"
      >
        <path d="M12 21c0-6 3-10 8-12-5-1-8 1-10 4-1-4-4-6-8-6 4 3 7 7 7 14z" />
      </svg>
      <p className="pp-message__text">{snapshot.coverMessage.text}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ pages */

/**
 * How the report is broken into sheets.
 *
 * One section, one page. Sections were briefly packed together to fill the
 * white space a thin report left at the bottom of each sheet; two headings
 * halfway down the same page read as cramped, and the white space was the
 * lesser problem — a studio with a normal amount of data fills the sheet
 * anyway. Only the cover message is positioned, and only within the cover.
 *
 * The one section that does flow across pages is the physical one, because
 * every metric in its table now draws a chart underneath: with nine metrics
 * that is more than a sheet holds. The heights below decide where it breaks.
 * They are calibrated against the rendered document and only ever need to be
 * *generous* — over-estimating costs a little white space, under-estimating
 * would push content past the bottom of a fixed-height page where
 * `overflow: hidden` would silently eat it. scripts/smoke-report.mjs asserts
 * that no page's content exceeds its box, so a bad estimate fails a test
 * rather than reaching a student.
 *
 * The template stays a pure function of the snapshot (§16.1) — nothing here
 * measures the DOM.
 */
const PAGE_CONTENT_HEIGHT = 900;

const H_TITLE = 76;
const H_TABLE_HEAD = 41;
const H_TABLE_ROW = 39;
/*
 * Added to every row's height when the table's "Başlangıç"/"Son Ölçüm"
 * headers can't name a shared date (§10.5): each row then prints its own
 * date under its value instead, one extra line taller than H_TABLE_ROW
 * accounts for. Missed once — a student with a custom metric measured on a
 * different day than the rest pushed every row's own-date line in, and the
 * resulting table ran taller than the estimate assumed, shoving a chart
 * into the footer.
 */
const H_TABLE_ROW_OWN_DATE = 15;
const H_CHART_ROW = 220;
const H_CHARTS_MARGIN = 20;

/** Rows of charts, two to a row, that still fit after `used` pixels. */
function chartRowsThatFit(used: number): number {
  const room = PAGE_CONTENT_HEIGHT - used - H_CHARTS_MARGIN;
  return Math.max(0, Math.floor(room / H_CHART_ROW));
}

function buildPages(
  snapshot: ReportSnapshot,
  resolveImage: ReportDocumentProps['resolveImage'],
  logoUrl: string | null | undefined,
): ReactNode[] {
  // Read from the snapshot, not from settings: a report keeps the motif it was
  // printed with, like everything else it froze.
  const ornamentKey = getOrnamentKey(snapshot.studio.ornamentKey);
  const pages: ((index: number, total: number) => ReactNode)[] = [];

  /* 1 — cover, always its own sheet */
  pages.push((index, total) => (
    <section className="pp-page pp-page--cover" key="cover">
      <CornerOrnament ornamentKey={ornamentKey} />
      <div className="pp-cover">
        <Brand snapshot={snapshot} logoUrl={logoUrl} large />

        <div className="pp-cover__main">
          <div className="pp-cover__identity">
            <h1 className="pp-cover__title">Gelişim Raporu</h1>
            <Divider />
            <p className="pp-cover__name">{snapshot.student.fullName}</p>

            <dl className="pp-cover__meta">
              <div>
                <dt>Raporlama Dönemi</dt>
                <dd>{formatPeriod(snapshot.period.start, snapshot.period.end)}</dd>
              </div>
              {snapshot.instructor && (
                <div>
                  <dt>Eğitmeniniz</dt>
                  <dd>{snapshot.instructor.name}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Only if the student has one; without it the identity column
              widens to the full width (§10.4). */}
          {snapshot.student.profilePhoto && (
            <div className="pp-cover__portrait">
              <img
                src={resolveImage(snapshot.student.profilePhoto) ?? undefined}
                alt={snapshot.student.fullName}
              />
            </div>
          )}
        </div>
      </div>

      {/* Sits at the foot of the cover rather than under the headline strip:
          it closes the page instead of interrupting it. */}
      <CoverMessage snapshot={snapshot} />
      <Footer snapshot={snapshot} page={index} total={total} />
    </section>
  ));

  /* 2 — physical progress: the table, then a chart for every row of it */
  if (snapshot.metrics.length + snapshot.customMetrics.length > 0) {
    /*
     * "Başlangıç" reads as the student's first ever measurement, which it has
     * not been since the period began bounding the data (§11.1) — on a report
     * covering two visits it is simply the earlier of the two. So the column
     * says which day it is.
     *
     * Only when every metric agrees, which is the ordinary case: they were all
     * taken at the same two visits. Where a metric was skipped at one of them
     * its own dates go in its row instead, and the headers stay generic rather
     * than claiming a date half the table does not share.
     */
    const allRows = [...snapshot.metrics, ...snapshot.customMetrics];
    const startDates = new Set(allRows.map((metric) => metric.start?.measuredAt ?? ''));
    const currentDates = new Set(allRows.map((metric) => metric.current.measuredAt));
    const sharedStart = startDates.size === 1 ? [...startDates][0] : null;
    const sharedCurrent = currentDates.size === 1 ? [...currentDates][0] : null;

    const rowHeight =
      sharedStart && sharedCurrent ? H_TABLE_ROW : H_TABLE_ROW + H_TABLE_ROW_OWN_DATE;
    const usedByTable =
      H_TITLE +
      H_TABLE_HEAD +
      (snapshot.metrics.length + snapshot.customMetrics.length) * rowHeight;

    // Charts run two to a row. However many rows are left under the table go
    // on this page; the rest continue on their own.
    const firstPageCharts = chartRowsThatFit(usedByTable) * 2;
    const perLaterPage = chartRowsThatFit(H_TITLE) * 2;

    const onFirst = snapshot.charts.slice(0, firstPageCharts);
    const rest = snapshot.charts.slice(firstPageCharts);

    pages.push((index, total) => (
      <Page
        snapshot={snapshot}
        logoUrl={logoUrl}
        ornamentKey={ornamentKey}
        index={index}
        total={total}
        key="physical"
        ornament
      >
        <SectionTitle icon="olcum">Fiziksel Gelişim</SectionTitle>

        <table className="pp-table">
          <thead>
            <tr>
              <th scope="col">Ölçüm</th>
              <th scope="col">{sharedStart ? formatDateShort(sharedStart) : 'Önceki'}</th>
              <th scope="col">
                {sharedCurrent ? formatDateShort(sharedCurrent) : 'Son Ölçüm'}
              </th>
              <th scope="col">Değişim</th>
            </tr>
          </thead>
          <tbody>
            {snapshot.metrics.map((metric) => (
              <tr key={metric.key}>
                <th scope="row">
                  <Icon name={metricIcon(metric.key)} size={18} />
                  {metric.label}
                </th>
                <td>
                  {metric.start
                    ? formatValue(metric.start.value, metric.unit, metric.decimals)
                    : '—'}
                  {/* Its own date, but only when it differs from the header. */}
                  {metric.start && !sharedStart && (
                    <em className="pp-table__when">
                      {formatDateShort(metric.start.measuredAt)}
                    </em>
                  )}
                </td>
                <td>
                  {formatValue(metric.current.value, metric.unit, metric.decimals)}
                  {!sharedCurrent && (
                    <em className="pp-table__when">
                      {formatDateShort(metric.current.measuredAt)}
                    </em>
                  )}
                </td>
                {/* Never red or green: falling weight is progress and rising
                      muscle mass is progress (§5.3). */}
                <td className="pp-delta">
                  {metric.changeAbs === null
                    ? '—'
                    : formatDelta(metric.changeAbs, metric.unit, metric.decimals)}
                </td>
              </tr>
            ))}

            {/* The studio's own metrics, under the built-in ones in the same
                table: they are measurements of the same body on the same day. */}
            {snapshot.customMetrics.map((metric) => (
              <tr key={metric.labelNormalized}>
                <th scope="row">{metric.label}</th>
                <td>
                  {metric.start
                    ? formatValue(metric.start.value, metric.unit, metric.decimals)
                    : '—'}
                  {metric.start && !sharedStart && (
                    <em className="pp-table__when">
                      {formatDateShort(metric.start.measuredAt)}
                    </em>
                  )}
                </td>
                <td>
                  {formatValue(metric.current.value, metric.unit, metric.decimals)}
                  {!sharedCurrent && (
                    <em className="pp-table__when">
                      {formatDateShort(metric.current.measuredAt)}
                    </em>
                  )}
                </td>
                <td className="pp-delta">
                  {metric.changeAbs === null
                    ? '—'
                    : formatDelta(metric.changeAbs, metric.unit, metric.decimals)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {onFirst.length > 0 && (
          <div className="pp-charts">
            {onFirst.map((chart) => (
              <TrendChart chart={chart} key={chart.metricKey} />
            ))}
          </div>
        )}
      </Page>
    ));

    for (const [i, group] of chunk(rest, Math.max(2, perLaterPage)).entries()) {
      pages.push((index, total) => (
        <Page
          snapshot={snapshot}
          logoUrl={logoUrl}
          ornamentKey={ornamentKey}
          index={index}
          total={total}
          key={`physical-charts-${i}`}
          ornament
        >
          {/* The subtitle matters: otherwise an identical heading reads as a
              second section rather than the rest of the one before it. */}
          <SectionTitle icon="olcum" subtitle="Grafikler (devam)">
            Fiziksel Gelişim
          </SectionTitle>
          <div className="pp-charts">
            {group.map((chart) => (
              <TrendChart chart={chart} key={chart.metricKey} />
            ))}
          </div>
        </Page>
      ));
    }
  }

  /* 3 — full body photos */
  for (const [i, group] of chunk(
    snapshot.fullBodyPhotoPairs,
    FULL_BODY_PER_PAGE,
  ).entries()) {
    // "Karşılaştırması" would be a lie on a page where nothing is compared.
    const comparing = group.some((pair) => pair.left !== null);
    pages.push((index, total) => (
      <Page
        snapshot={snapshot}
        logoUrl={logoUrl}
        ornamentKey={ornamentKey}
        index={index}
        total={total}
        key={`body-${i}`}
        ornament
      >
        <SectionTitle
          icon="fotograf"
          subtitle={comparing ? 'Tam Vücut Karşılaştırması' : undefined}
        >
          Gelişim Fotoğrafları
        </SectionTitle>
        <div className="pp-pairs">
          {group.map((pair) => (
            <PhotoPair
              pair={pair}
              resolveImage={resolveImage}
              variant="body"
              key={pair.right.photoId}
            />
          ))}
        </div>
      </Page>
    ));
  }

  /* 4 — regional photos */
  const regionalGroups = chunk(snapshot.regionalPhotoPairs, REGIONAL_PER_PAGE);
  for (const [i, group] of regionalGroups.entries()) {
    const last = i === regionalGroups.length - 1;
    const withNote = last && snapshot.regionalNote;
    pages.push((index, total) => (
      <Page
        snapshot={snapshot}
        logoUrl={logoUrl}
        ornamentKey={ornamentKey}
        index={index}
        total={total}
        key={`regional-${i}`}
        ornament
      >
        <SectionTitle icon="fotograf">Bölgesel Gelişim Fotoğrafları</SectionTitle>
        <div className="pp-grid2">
          {group.map((pair) => (
            <div className="pp-card pp-region" key={pair.right.photoId}>
              <PhotoPair pair={pair} resolveImage={resolveImage} variant="regional" />
            </div>
          ))}
        </div>

        {/* Constitution §8: the instructor's own words, or nothing.
            The system contributes no wording on this page. */}
        {withNote && (
          <div className="pp-card pp-note">
            <span className="pp-note__label">Genel Değerlendirme</span>
            <p>{snapshot.regionalNote}</p>
          </div>
        )}
      </Page>
    ));
  }

  /* 5 — goals */
  for (const [i, group] of chunk(snapshot.goals, GOALS_PER_PAGE).entries()) {
    pages.push((index, total) => (
      <Page
        snapshot={snapshot}
        logoUrl={logoUrl}
        ornamentKey={ornamentKey}
        index={index}
        total={total}
        key={`goals-${i}`}
        ornament
      >
        <SectionTitle icon="hedef">Hedefler</SectionTitle>
        <ul className="pp-goals">
          {group.map((g) => (
            <li className="pp-card pp-goal" key={g.goalId}>
              <span className="pp-goal__no">
                <Icon name={goalIcon(g.goalType)} size={20} />
              </span>
              <div className="pp-goal__body">
                <span className="pp-goal__title">{g.title}</span>
                {g.mode === 'numeric' ? (
                  <div className="pp-goal__numeric">
                    <div className="pp-goal__values">
                      <span>
                        <em>Başlangıç</em>
                        {g.startValue === null
                          ? '—'
                          : formatValue(g.startValue, g.unit ?? '', 1)}
                      </span>
                      <span>
                        <em>Güncel</em>
                        {g.currentValue === null
                          ? '—'
                          : formatValue(g.currentValue, g.unit ?? '', 1)}
                      </span>
                      <span>
                        <em>Hedef</em>
                        {g.targetValue === null
                          ? '—'
                          : formatValue(g.targetValue, g.unit ?? '', 1)}
                      </span>
                    </div>
                    {g.progressPct !== null && (
                      <div className="pp-goal__progress">
                        <div className="pp-goal__bar">
                          <div
                            className="pp-goal__fill"
                            style={{ width: `${g.progressPct}%` }}
                          />
                        </div>
                        <span className="pp-goal__pct">
                          {formatPercent(g.progressPct)}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  g.qualitativeNote && (
                    <p className="pp-goal__note">{g.qualitativeNote}</p>
                  )
                )}
              </div>
            </li>
          ))}
        </ul>
      </Page>
    ));
  }

  /*
   * 5.5 — posture analysis: one full sheet per chosen session, oldest first
   * (buildPosture already sorts the array), nothing else on each sheet. A
   * compact multi-session comparison layout was tried and reverted — see
   * docs/superpowers/specs/2026-10-03-posture-sequential-pages-design.md —
   * because meaningfully large photos and "everything on one page" cannot
   * both hold at once; this keeps every photo at full, legible size instead.
   */
  for (const [i, assessment] of snapshot.posture.entries()) {
    pages.push((index, total) => (
      <Page
        snapshot={snapshot}
        logoUrl={logoUrl}
        ornamentKey={ornamentKey}
        index={index}
        total={total}
        key={`posture-${i}`}
        ornament={false}
      >
        <SectionTitle icon="postur" subtitle={formatDateLong(assessment.assessedAt)}>
          Postür Analizi
        </SectionTitle>

        <PosturePageSingle assessment={assessment} resolveImage={resolveImage} />
      </Page>
    ));
  }

  /* 6 — instructor assessment */
  const assessment = snapshot.assessment;
  /*
   * Pinned to the foot of the last sheet. It is the one block on the page that
   * is not about the student's progress, and a note about renewing a package
   * belongs under the report rather than in the middle of it.
   */
  const packageReminder = snapshot.packageReminder.enabled && (
    <div className="pp-card pp-note pp-package">
      <span className="pp-note__label">Paket Bilgilendirmesi</span>
      <p>
        Paketinizde <strong>{snapshot.packageReminder.remainingLessons}</strong> ders
        hakkınız kalmıştır. Paket yenileme seçenekleri için eğitmeninizle iletişime
        geçebilirsiniz.
      </p>
    </div>
  );

  if (assessment) {
    const anyPrevious = assessment.scores.some((s) => s.previous !== null);
    pages.push((index, total) => (
      <Page
        snapshot={snapshot}
        logoUrl={logoUrl}
        ornamentKey={ornamentKey}
        index={index}
        total={total}
        key="assessment"
        ornament
      >
        {/* This heading is mandatory and must never read as a clinical
            result (Constitution §10, §10.10). */}
        <SectionTitle icon="degerlendirme">Eğitmen Değerlendirmesi</SectionTitle>

        <table className="pp-scores">
          <thead>
            <tr>
              <th scope="col">
                <span className="pp-visually-hidden">Başlık</span>
              </th>
              {anyPrevious && <th scope="col">Önceki Dönem</th>}
              <th scope="col">Bu Dönem</th>
            </tr>
          </thead>
          <tbody>
            {assessment.scores.map((score) => (
              <tr key={score.key}>
                <th scope="row">
                  <Icon name={assessmentIcon(score.key)} size={18} />
                  {score.label}
                </th>
                {anyPrevious && (
                  <td>
                    {score.previous === null ? (
                      '—'
                    ) : (
                      <ScoreBar value={score.previous} muted />
                    )}
                  </td>
                )}
                <td>
                  <ScoreBar value={score.current} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pp-notes">
          {assessment.generalNote && (
            <div className="pp-card pp-note">
              <span className="pp-note__label">Genel Değerlendirme</span>
              <p>{assessment.generalNote}</p>
            </div>
          )}
          {assessment.nextPeriodFocus && (
            <div className="pp-card pp-note">
              <span className="pp-note__label">Bir Sonraki Dönem Odağı</span>
              <p>{assessment.nextPeriodFocus}</p>
            </div>
          )}
        </div>
        {packageReminder}
      </Page>
    ));
  } else if (packageReminder) {
    // A reminder with no assessment to sit under still gets a sheet: it is
    // the one thing on the page, so it says what it is.
    pages.push((index, total) => (
      <Page
        snapshot={snapshot}
        logoUrl={logoUrl}
        ornamentKey={ornamentKey}
        index={index}
        total={total}
        key="package"
        ornament
      >
        <SectionTitle icon="paket">Paket Bilgilendirmesi</SectionTitle>
        {packageReminder}
      </Page>
    ));
  }

  const total = pages.length;
  return pages.map((render, i) => render(i + 1, total));
}

function ScoreBar({ value, muted = false }: { value: number; muted?: boolean }) {
  return (
    <span className={muted ? 'pp-score pp-score--muted' : 'pp-score'}>
      <span className="pp-score__value">
        {value}
        <em>/5</em>
      </span>
      <span className="pp-score__track">
        <span className="pp-score__fill" style={{ width: `${(value / 5) * 100}%` }} />
      </span>
    </span>
  );
}

export function ReportDocument({ snapshot, resolveImage, logoUrl }: ReportDocumentProps) {
  const palette = getPalette(snapshot.studio.paletteKey);
  const style = paletteToCssVars(palette) as CSSProperties;

  return (
    <div className="pp-report" style={style} lang="tr">
      {buildPages(snapshot, resolveImage, logoUrl)}
    </div>
  );
}
